// Printing for the pallet desk. Every pallet document is a file (the
// Palletways label is a PDF, attachments are PDF / PNG / JPG), so it goes
// through the browser's print dialog via printPdfBase64 — the print agent
// takes HTML or ZPL only. When no dialog can open the PDF is downloaded
// instead, so a booking never ends up without its label.
import { toast } from "react-toastify";
import { apiFetch } from "../../api/apiFetch";
import { printPdfBase64, downloadPdfBase64 } from "../helpers/carrierLabelPrint";
import { isPdfAttachment, type DeskAttachment } from "./palletDesk";

type Translate = (key: string, vars?: Record<string, string>) => string;

/** Print a base64 PDF; `filename` is what it is saved as when printing is
 *  not possible. Resolves true when a dialog opened, false when it fell
 *  back to a download. */
export const printPdf = async (label: { contentBase64?: string } | null | undefined, filename: string, t: Translate): Promise<boolean> => {
    if (!label?.contentBase64) return false;
    const printed = await printPdfBase64(label.contentBase64);
    if (!printed) {
        downloadPdfBase64(label.contentBase64, filename);
        toast.warn(t("pallet_dispatch_print_fallback"), { toastId: "pallet-dispatch-print-fallback" });
    }
    return printed;
};

export const palletwaysFilename = (conNo: string | null | undefined) => `palletways-${conNo ?? "label"}.pdf`;

/** A direct carrier's document (JAS): "jas-label-4012123412.pdf", "jas-waybill-4012123412.pdf". */
export const directFilename = (carrier: string, ref: string | null | undefined, kind: "label" | "waybill" = "label") =>
    `${carrier.toLowerCase().replace(/\s+/g, "-") || "carrier"}-${kind}-${ref ?? "document"}.pdf`;

/** The attachment's bytes, or null when it cannot be fetched (404 = gone). */
export const fetchAttachment = async (orderId: string, att: DeskAttachment): Promise<{ blob: Blob } | { error: "no_booking" | "generic" }> => {
    const res = await apiFetch(`/orders/${encodeURIComponent(orderId)}/attachments/${encodeURIComponent(att.attachment_id)}/download`, {});
    if (!res.ok) return { error: res.status === 404 ? "no_booking" : "generic" };
    return { blob: await res.blob() };
};

const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? "").split(",")[1] ?? "");
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
    });

/** Open the file in its own tab (viewing, or printing a non-PDF from there). */
export const openBlobInTab = (blob: Blob) => {
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener");
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

/** Print an attachment: PDFs through the label print path, anything else
 *  opens in a tab to print from there. */
export const printAttachment = async (orderId: string, att: DeskAttachment, t: Translate): Promise<{ ok: true } | { ok: false; error: "no_booking" | "generic" }> => {
    const fetched = await fetchAttachment(orderId, att);
    if ("error" in fetched) return { ok: false, error: fetched.error };
    if (isPdfAttachment(att)) {
        const b64 = await blobToBase64(fetched.blob);
        await printPdf({ contentBase64: b64 }, /\.pdf$/i.test(att.filename) ? att.filename : `${att.filename}.pdf`, t);
        return { ok: true };
    }
    openBlobInTab(fetched.blob);
    return { ok: true };
};

export const viewAttachment = async (orderId: string, att: DeskAttachment): Promise<{ ok: true } | { ok: false; error: "no_booking" | "generic" }> => {
    const fetched = await fetchAttachment(orderId, att);
    if ("error" in fetched) return { ok: false, error: fetched.error };
    openBlobInTab(fetched.blob);
    return { ok: true };
};
